import { useState } from "react";
import { useIntl } from "react-intl";
import { Button } from "./Button";

// Cancel + confirm pair at the foot of every Entry edit panel. The two
// EditableRow + CareerGraph RoleEditor sites used to host their own copy
// of this block (state, tooltip, disabled wiring). Same affordance, same
// validation gating, same workshop styling - consolidated here.
//
// `missingFields` is the externally-computed list of required-field
// labels still empty. When non-empty:
//   - confirm is disabled
//   - clicking confirm reveals a tooltip listing the labels
// Caller never sees the tooltip state; it's local and resets on close.
//
// Neither button saves: confirm just signals "I'm done with this panel"
// (close), cancel signals "drop my pending overlay". The page-level
// SaveBar owns commit (@adr SPARK-0005-unified-deferred-save-bar@1).

interface EditPanelButtonsProps {
  /**
   * Anker-Präfix des Kastens; die drei Knöpfe leiten `-confirm`, `-cancel` und
   * `-delete` davon ab. VOM AUFRUFER, NICHT FEST: mehrere Bearbeitungskästen
   * können gleichzeitig offen sein (zwei Zeilen im Profil, eine Zeile plus die
   * Kompetenzleiter) — eine feste Kennung gäbe es dann mehrfach.
   */
  id?: string;
  onCancel: () => void;
  onConfirm: () => void;
  /**
   * Stage a delete of the row. When omitted, the Löschen button doesn't
   * render - drafts pass undefined since ABBRECHEN removes them anyway.
   * The handler is responsible for closing the panel; the primitive does
   * not call onConfirm/onCancel after delete.
   */
  onDelete?: () => void;
  cancelLabel?: string;
  confirmLabel?: string;
  deleteLabel?: string;
  missingFields?: readonly string[];
}

export function EditPanelButtons({
  onCancel,
  onConfirm,
  onDelete,
  cancelLabel,
  confirmLabel,
  deleteLabel,
  missingFields = [],
  id,
}: EditPanelButtonsProps) {
  const intl = useIntl();
  const cancelText = cancelLabel ?? intl.formatMessage({ id: "common.cancel", defaultMessage: "Cancel" });
  const confirmText = confirmLabel ?? intl.formatMessage({ id: "search.done", defaultMessage: "Done" });
  const deleteText = deleteLabel ?? intl.formatMessage({ id: "common.delete", defaultMessage: "Delete" });
  const [showMissing, setShowMissing] = useState(false);
  const tooltipVisible = showMissing && missingFields.length > 0;

  const handleConfirm = () => {
    if (missingFields.length > 0) {
      setShowMissing(true);
      return;
    }
    onConfirm();
  };

  return (
    <div className="flex items-center justify-between gap-2 pt-1">
      <div>
        {onDelete ? (
          <Button id={id ? `${id}-delete` : undefined} variant="secondary" onClick={onDelete}>
            {deleteText}
          </Button>
        ) : null}
      </div>
      <div className="flex items-center gap-2">
        <Button id={id ? `${id}-cancel` : undefined} variant="secondary" onClick={onCancel}>
          {cancelText}
        </Button>
        <div className="relative">
          {tooltipVisible ? (
            <div
              id={id ? `${id}-missing` : undefined}
              role="tooltip"
              className="absolute bottom-full right-0 mb-2 w-max max-w-[240px] rounded-md bg-page px-3 py-2 text-left text-smycles-label leading-snug text-ink-accent shadow-lg ring-1 ring-accent-decoration/40"
            >
              {intl.formatMessage({ id: "design.stillFillIn", defaultMessage: "Please still fill in: {fields}" }, { fields: missingFields.join(", ") })}
            </div>
          ) : null}
          <Button
            id={id ? `${id}-confirm` : undefined}
            variant="primary"
            onClick={handleConfirm}
            disabled={missingFields.length > 0}
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>
  );
}
